import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { GroupNode } from '../../core/utils/grouping.util';

/**
 * Group Row Component
 *
 * Renders a group row in the grid with expand/collapse functionality,
 * indentation, count, and optional aggregates.
 *
 * @example
 * ```html
 * <app-group-row
 *   [groupNode]="node"
 *   [expanded]="isExpanded(node.key)"
 *   [indentSize]="20"
 *   (toggle)="onToggleGroup(node.key)">
 * </app-group-row>
 * ```
 */
@Component({
  selector: 'app-group-row',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  template: `
    <div
      class="group-row-container"
      [style.padding-left.px]="getIndentation()"
      [class.group-row-expanded]="expanded">
      <!-- Expand/Collapse Button -->
      <button
        mat-icon-button
        class="group-expand-button"
        (click)="onToggle($event)">
        <mat-icon class="group-expand-icon">
          {{ expanded ? 'expand_more' : 'chevron_right' }}
        </mat-icon>
      </button>

      <!-- Group Icon -->
      <mat-icon class="group-icon">folder</mat-icon>

      <!-- Group Label -->
      <span class="group-label">
        {{ groupNode.label }}
      </span>

      <!-- Group Count Badge -->
      <span class="group-count-badge">
        ({{ groupNode.count }})
      </span>

      <!-- Aggregates (optional) -->
      <div *ngIf="groupNode.aggregates && showAggregates" class="group-aggregates">
        <span
          *ngFor="let agg of getAggregatesArray()"
          class="group-aggregate-item">
          {{ agg.label }}: <strong>{{ formatAggregate(agg.value) }}</strong>
        </span>
      </div>
    </div>
  `,
  styles: [`
    .group-row-container {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      background-color: #f9fafb;
      border-bottom: 1px solid #e5e7eb;
      min-height: 48px;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: #f3f4f6;
      }

      &.group-row-expanded {
        background-color: #f3f4f6;
      }

      // Dark mode
      :host-context(.dark) & {
        background-color: #1f2937;
        border-bottom-color: #374151;

        &:hover {
          background-color: #111827;
        }

        &.group-row-expanded {
          background-color: #111827;
        }
      }
    }

    .group-expand-button {
      width: 32px !important;
      height: 32px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .group-expand-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
      transition: transform 0.2s ease;
    }

    .group-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
      color: #6b7280;
      flex-shrink: 0;

      :host-context(.dark) & {
        color: #9ca3af;
      }
    }

    .group-label {
      font-weight: 600;
      font-size: 14px;
      color: #111827;
      flex-shrink: 0;

      :host-context(.dark) & {
        color: #f9fafb;
      }
    }

    .group-count-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 2px 8px;
      border-radius: 12px;
      background-color: #dbeafe;
      color: #1e40af;
      font-size: 12px;
      font-weight: 600;
      flex-shrink: 0;

      :host-context(.dark) & {
        background-color: #1e3a8a;
        color: #bfdbfe;
      }
    }

    .group-aggregates {
      display: flex;
      gap: 12px;
      margin-left: auto;
      flex-shrink: 0;
    }

    .group-aggregate-item {
      font-size: 12px;
      color: #6b7280;
      white-space: nowrap;

      strong {
        color: #111827;
        font-weight: 600;
      }

      :host-context(.dark) & {
        color: #9ca3af;

        strong {
          color: #f9fafb;
        }
      }
    }
  `],
})
export class GroupRowComponent<T> {
  /**
   * Group node data
   */
  @Input() groupNode!: GroupNode<T>;

  /**
   * Whether the group is expanded
   */
  @Input() expanded: boolean = false;

  /**
   * Indent size per level (pixels)
   * @default 20
   */
  @Input() indentSize: number = 20;

  /**
   * Show aggregates in group row
   * @default true
   */
  @Input() showAggregates: boolean = true;

  /**
   * Emitted when user clicks expand/collapse button
   */
  @Output() toggle = new EventEmitter<void>();

  /**
   * Get indentation based on group level
   */
  getIndentation(): number {
    return this.groupNode.level * this.indentSize;
  }

  /**
   * Handle toggle click
   */
  onToggle(event: Event): void {
    event.stopPropagation();
    this.toggle.emit();
  }

  /**
   * Get aggregates as array for iteration
   */
  getAggregatesArray(): Array<{ label: string; value: any }> {
    if (!this.groupNode.aggregates) {
      return [];
    }

    return Object.entries(this.groupNode.aggregates).map(([key, value]) => ({
      label: this.formatAggregateLabel(key),
      value,
    }));
  }

  /**
   * Format aggregate label for display
   */
  private formatAggregateLabel(key: string): string {
    // Common aggregate labels
    const labelMap: Record<string, string> = {
      count: 'Cantidad',
      sum: 'Suma',
      avg: 'Promedio',
      min: 'Mínimo',
      max: 'Máximo',
      first: 'Primero',
      last: 'Último',
    };

    // Try to extract function from key (e.g., "total_sum" -> "sum")
    const parts = key.split('_');
    const func = parts[parts.length - 1];

    if (labelMap[func]) {
      const field = parts.slice(0, -1).join('_');
      return `${this.humanize(field)} (${labelMap[func]})`;
    }

    return this.humanize(key);
  }

  /**
   * Format aggregate value for display
   */
  formatAggregate(value: any): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    // Format numbers with thousands separator
    if (typeof value === 'number') {
      return value.toLocaleString('es-HN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }

    return String(value);
  }

  /**
   * Humanize field name
   */
  private humanize(field: string): string {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/\./g, ' ')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}
